import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { Copy } from "iconsax-reactjs";
import { QRLogo, SecureAuth } from "asset/icons";
import Input from "component/form/Input";
import { copyField } from "utilities/clipboard";
import BackupCodesModal from "./BackupCodesModal";

const getBackupCodes = (data) => {
    if (Array.isArray(data?.backupCodes)) return data.backupCodes;
    if (Array.isArray(data?.backup_codes)) return data.backup_codes;
    if (Array.isArray(data?.recoveryCodes)) return data.recoveryCodes;
    if (Array.isArray(data?.recovery_codes)) return data.recovery_codes;
    return [];
};

const Setup2FA = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { users } = useSelector((state) => state);
    const resetSetupData = location.state?.setupData || null;
    const [verificationCode, setVerificationCode] = useState("");
    const [setupData, setSetupData] = useState(resetSetupData);
    const [loading, setLoading] = useState(!resetSetupData);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showBackupModal, setShowBackupModal] = useState(false);
    const [hasSavedBackupCodes, setHasSavedBackupCodes] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const fetchMfaSetup = async () => {
            if (resetSetupData) {
                setErrorMessage("");
                setLoading(false);
                return;
            }

            setLoading(true);
            setErrorMessage("");

            try {
                const response = await axios.get("/v1/users/mfa-setup");
                const data = response?.data?.data || response?.data || {};

                if (isMounted) {
                    setSetupData(data);
                }
            } catch (error) {
                if (isMounted) {
                    setErrorMessage(
                        error?.response?.data?.message ||
                            "Unable to load two-factor authentication setup."
                    );
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchMfaSetup();

        return () => {
            isMounted = false;
        };
    }, [resetSetupData]);

    const secretKey =
        setupData?.secretKey || setupData?.secret || setupData?.mfaSecret || setupData?.otpSecret;
    const backupCodes = getBackupCodes(setupData);
    const accountEmail = users?.email || setupData?.email || "user@tradevu.com";

    const qrValue = useMemo(
        () =>
            setupData?.uri ||
            setupData?.otpauthUrl ||
            setupData?.otpAuthUrl ||
            setupData?.qrCode ||
            setupData?.qrCodeUrl ||
            (secretKey
                ? `otpauth://totp/Tradevu:${accountEmail}?secret=${secretKey}&issuer=Tradevu&algorithm=SHA1&digits=6&period=30`
                : ""),
        [accountEmail, secretKey, setupData]
    );

    const handleVerificationCodeChange = (inputValue) => {
        const value = String(inputValue || "")
            .replace(/\D/g, "")
            .slice(0, 6);
        setVerificationCode(value);
    };

    const handleCloseBackupModal = () => {
        if (!hasSavedBackupCodes) return;
        setShowBackupModal(false);
        navigate("/dashboard");
    };

    const markBackupCodesAsSaved = () => {
        setHasSavedBackupCodes(true);
    };

    const handleCopyBackupCodes = async () => {
        const copied = await copyField(backupCodes.join("\n"), "Backup codes");
        if (copied) {
            markBackupCodesAsSaved();
        }
    };

    const handleDownloadBackupCodes = () => {
        if (!backupCodes.length) return;

        const blob = new Blob([backupCodes.join("\n")], { type: "text/plain;charset=utf-8" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = "tradevu-backup-codes.txt";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        markBackupCodesAsSaved();
    };

    const handlePrintBackupCodes = () => {
        if (!backupCodes.length) return;

        const printWindow = window.open("", "_blank", "width=800,height=600");
        if (!printWindow) return;

        printWindow.document.write(`
            <html>
                <head>
                    <title>Tradevu Backup Codes</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 32px; color: #161827; }
                        h1 { font-size: 28px; margin-bottom: 12px; }
                        p { font-size: 16px; line-height: 1.6; }
                        ul { margin-top: 24px; columns: 2; padding-left: 20px; }
                        li { margin-bottom: 12px; font-size: 18px; }
                    </style>
                </head>
                <body>
                    <h1>Tradevu Backup Codes</h1>
                    <p>Keep these codes safe and do not share them. Each code can only be used once.</p>
                    <ul>${backupCodes.map((code) => `<li>${code}</li>`).join("")}</ul>
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        markBackupCodesAsSaved();
    };

    const handleSubmit = async () => {
        if (verificationCode.length !== 6) return;

        setIsSubmitting(true);
        setErrorMessage("");

        try {
            await axios.post("/v1/users/mfa-verify", {
                code: verificationCode,
            });

            setShowBackupModal(true);
        } catch (error) {
            setErrorMessage(
                error?.response?.data?.message ||
                    "Unable to verify your authentication code. Please try again."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="bg-white rounded-xl w-full max-w-[650px] mx-auto border border-[#EBEDF9]">
                <div className="!pt-5">
                    <SecureAuth />
                </div>

                <div className="p-8 !pt-0">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">
                            Set up two-factor authentication
                        </h2>
                        <p className="aku-body-normal-regular text-[#383B56] mt-2">
                            Scan the QR code with your preferred authenticator app for additional
                            security.
                        </p>
                        <button
                            type="button"
                            className="text-[#6F00FF] text-sm font-medium mt-3 hover:underline"
                        >
                            What is an authenticator app?
                        </button>
                    </div>

                    {errorMessage && (
                        <div className="mt-4 rounded-lg border border-[#FFD6D6] bg-[#FFF5F5] p-3 text-sm text-[#D92D20]">
                            {errorMessage}
                        </div>
                    )}

                    <div className="mt-6">
                        <div className="flex flex-col !gap-5 sm:flex-row sm:items-center">
                            <div className="flex justify-center sm:justify-start">
                                <div className="relative rounded-xl border border-[#E4DFFF] bg-white p-3 shadow-sm">
                                    {loading ? (
                                        <div className="flex h-[180px] w-[180px] items-center justify-center text-sm text-[#667085]">
                                            Loading QR...
                                        </div>
                                    ) : qrValue ? (
                                        <QRCode
                                            value={qrValue}
                                            size={180}
                                            fgColor="#7F3DFF"
                                            bgColor="#ffffff"
                                            style={{ height: 180, width: 180 }}
                                        />
                                    ) : (
                                        <div className="flex h-[180px] w-[180px] items-center justify-center text-center text-sm text-[#667085]">
                                            QR code unavailable
                                        </div>
                                    )}
                                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                        <div className="rounded-xl bg-white p-2 shadow-sm">
                                            <QRLogo className="h-7 w-7" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <p className="aku-body-small-regular text-[#383B56]">
                                    You can also paste this secret key in the app
                                </p>
                                <p className="mt-2 break-all aku-body-normal-medium text-[#161827]">
                                    {loading ? "Loading..." : secretKey || "Secret key unavailable"}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => copyField(secretKey, "Secret key")}
                                    disabled={!secretKey}
                                    className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-[#6F00FF] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <Copy size="18" color="#6F00FF" />
                                    Copy secret key
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6">
                        <Input
                            type="text"
                            label="Verification code"
                            labelClassName="!mt-0"
                            value={verificationCode}
                            onChange={handleVerificationCodeChange}
                            placeholder="Enter 6-digit code from app"
                            maxLength="6"
                        />
                    </div>

                    <div className="flex flex-col gap-4 mt-6 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="text-[#6F00FF] text-sm font-medium hover:underline"
                            >
                                Setup later
                            </button>
                            <button type="button" className="text-sm text-gray-500 hover:underline">
                                Need help?
                            </button>
                        </div>

                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={verificationCode.length !== 6 || isSubmitting || loading}
                            className={`bg-[#6F00FF] text-white py-2 px-6 rounded-lg hover:bg-[#6f00fff0] transition ${
                                verificationCode.length !== 6 || isSubmitting || loading
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                            }`}
                        >
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </button>
                    </div>
                </div>
            </div>

            <BackupCodesModal
                isOpen={showBackupModal}
                backupCodes={backupCodes}
                hasSavedBackupCodes={hasSavedBackupCodes}
                onClose={handleCloseBackupModal}
                onCopy={handleCopyBackupCodes}
                onDownload={handleDownloadBackupCodes}
                onPrint={handlePrintBackupCodes}
                onRegenerate={() => {}}
            />
        </>
    );
};

export default Setup2FA;
